import type { ApplicationQueueView, ApplicationState } from '@/types';

/** Per-queue list UI state (selection, page, sort, status pin). */
export type ApplicationQueueListSlice = {
  page: number;
  pinnedStatus: ApplicationState | null;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  selectedIds: string[];
};

const APPLICATION_STATES = new Set<ApplicationState>([
  'APPLIED_NEW',
  'PENDING_UPGRADE',
  'READY_TO_PROCESS',
  'PIPELINE_RUNNING',
  'AWAITING_DECISION',
  'PIPELINE_ERROR',
  'REJECTED',
  'HIRED',
  'OFFER_DECLINED',
  'WITHDRAWN',
]);

function normalizeSelectedIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Bump when default list sort changes — migrates persisted `appliedAt` → `updatedAt`. */
export const APPLICATIONS_LIST_SORT_VERSION = 2;

const DEFAULT_QUEUE_SLICE: ApplicationQueueListSlice = {
  page: 1,
  pinnedStatus: null,
  sortColumn: 'updatedAt',
  sortDirection: 'desc',
  selectedIds: [],
};

/** Legacy default before APPLICATIONS_LIST_SORT_VERSION 2. */
const LEGACY_DEFAULT_SORT_COLUMN = 'appliedAt';

/** One-time upgrade for slices still on the old default (Applied ↓). */
export function migrateQueueSliceSort(slice: ApplicationQueueListSlice): ApplicationQueueListSlice {
  if (
    slice.sortColumn === LEGACY_DEFAULT_SORT_COLUMN
    && slice.sortDirection === 'desc'
  ) {
    return { ...slice, sortColumn: 'updatedAt' };
  }
  return slice;
}

/** Coerce partial / legacy session JSON into a safe slice (avoids `new Set(null)` crashes). */
export function normalizeQueueSlice(
  raw: Partial<ApplicationQueueListSlice> | null | undefined,
  fallback: ApplicationQueueListSlice = DEFAULT_QUEUE_SLICE,
): ApplicationQueueListSlice {
  const base = fallback;
  if (!raw) return { ...base, selectedIds: [...base.selectedIds] };

  const page =
    typeof raw.page === 'number' && Number.isFinite(raw.page) && raw.page >= 1
      ? Math.floor(raw.page)
      : base.page;

  const pinned =
    typeof raw.pinnedStatus === 'string' && APPLICATION_STATES.has(raw.pinnedStatus as ApplicationState)
      ? (raw.pinnedStatus as ApplicationState)
      : base.pinnedStatus;

  const sortColumn = typeof raw.sortColumn === 'string' && raw.sortColumn.length > 0
    ? raw.sortColumn
    : base.sortColumn;

  const sortDirection = raw.sortDirection === 'asc' || raw.sortDirection === 'desc'
    ? raw.sortDirection
    : base.sortDirection;

  const selectedIds = normalizeSelectedIds(raw.selectedIds);
  const mergedSelected = selectedIds.length > 0 ? selectedIds : normalizeSelectedIds(base.selectedIds);

  return {
    page,
    pinnedStatus: pinned,
    sortColumn,
    sortDirection,
    selectedIds: mergedSelected,
  };
}

function applySortVersionMigration(
  slice: ApplicationQueueListSlice,
  sortVersion?: number,
): ApplicationQueueListSlice {
  if (sortVersion != null && sortVersion >= APPLICATIONS_LIST_SORT_VERSION) {
    return slice;
  }
  return migrateQueueSliceSort(slice);
}

export function createDefaultQueueSlice(
  overrides?: Partial<ApplicationQueueListSlice>,
): ApplicationQueueListSlice {
  return normalizeQueueSlice(overrides, DEFAULT_QUEUE_SLICE);
}

type LegacyPersistShape = {
  queueView?: ApplicationQueueView;
  page?: number;
  pinnedStatus?: string | null;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  selectedIds?: string[];
  active?: ApplicationQueueListSlice;
  archive?: ApplicationQueueListSlice;
};

export function loadQueueSlicesFromPersist(
  saved: LegacyPersistShape | null,
  queueView: ApplicationQueueView = saved?.queueView ?? 'active',
  options?: { forceDefaultSort?: boolean },
): {
  queueView: ApplicationQueueView;
  active: ApplicationQueueListSlice;
  archive: ApplicationQueueListSlice;
} {
  const freshSort = createDefaultQueueSlice();
  const sortVersion = (saved as { listSortVersion?: number } | null)?.listSortVersion;
  const legacy = options?.forceDefaultSort
    ? freshSort
    : applySortVersionMigration(
      normalizeQueueSlice({
        page: saved?.page,
        pinnedStatus: saved?.pinnedStatus as ApplicationState | null | undefined,
        sortColumn: saved?.sortColumn,
        sortDirection: saved?.sortDirection,
        selectedIds: normalizeSelectedIds(saved?.selectedIds),
      }),
      sortVersion,
    );

  const resolvedView: ApplicationQueueView =
    queueView === 'archive' ? 'archive' : 'active';

  const active = options?.forceDefaultSort
    ? freshSort
    : applySortVersionMigration(
      saved?.active ? normalizeQueueSlice(saved.active, legacy) : legacy,
      sortVersion,
    );
  const archive = options?.forceDefaultSort
    ? freshSort
    : applySortVersionMigration(
      saved?.archive ? normalizeQueueSlice(saved.archive) : normalizeQueueSlice({}),
      sortVersion,
    );

  return {
    queueView: resolvedView,
    active,
    archive,
  };
}

export function captureQueueSlice(input: {
  page: number;
  pinnedStatus: ApplicationState | null;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  selectedIds: Set<string>;
}): ApplicationQueueListSlice {
  return {
    page: input.page,
    pinnedStatus: input.pinnedStatus,
    sortColumn: input.sortColumn,
    sortDirection: input.sortDirection,
    selectedIds: [...input.selectedIds],
  };
}
